import { useMutation, useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { gql } from '../graphql/generated/gql';
import { UserRole } from '../topic/topicPolicyCheck';
import { useAllTopicMap } from '../topic/useAllTopics';
import NestableLink from '../utils/NestableLink';
import populateObjects from '../utils/populateObjects';
import postPath, { publicPostPath } from '../utils/url/postPath';
import useIsPublicView from '../utils/useIsPublicView';
import { READ_POST_MUTATION } from './PostActivityTracker';
import styles from './PostDetailPreview.module.scss';
import PostDetailPreviewStateBar, {
  hasStateBar,
} from './PostDetailPreviewStateBar';
import PostPreviewQuillComponent from './PostPreviewQuillComponent';
import {
  isAllowedThroughLinkAccess,
  PostPolicyAction,
} from './postPolicyCheck';
import PostTopicListWithBreadcrumbs, {
  hasBreadcrumbs,
} from './topic/PostTopicListWithBreadcrumbs';

const MIN_ACTIVITY_READ = 10_000;

gql(`
  fragment PostDetailPreview_post on PublicPostFields {
    id
    content
    version
    topics {
      id
    }
    ... on Post {
      banner {
        original
      }
    }
    ... on PublicPost {
      banner {
        original
      }
    }
  }
`);

const POST_DETAIL_PREVIEW_QUERY = gql(`
  query PostDetailPreview(
    $id: ID!
    $queryId: String
    $resultId: String
    $position: Int
  ) {
    post(
      id: $id
      view: true
      queryId: $queryId
      resultId: $resultId
      position: $position
    ) {
      id
      ...PostDetailPreview_post
      ...isAllowedThroughLinkAccess_post
      ...postPath_post
      ...PostDetailPreviewStateBar_post
    }
  }
`);

const PUBLIC_POST_DETAIL_PREVIEW_QUERY = gql(`
  query PublicPostDetailPreview($id: ID!) { 
    post: publicPost(id: $id) {
      id
      ...PostDetailPreview_post
      ...isAllowedThroughLinkAccess_post
      ...publicPostPath_post
    }
  }
`);

interface PostDetailPreviewBannerProps {
  banner: string;
}

export const PostDetailPreviewBanner = ({
  banner,
}: PostDetailPreviewBannerProps) => (
  <div
    className={styles.banner}
    style={{ backgroundImage: `url(${banner})` }}
  />
);

type PostDetailPreviewProps = {
  onPreviewLoadChange?: (loaded: boolean) => void;
  onClick?: () => void;
  queryId?: string;
  resultId?: string;
  position?: number;
  postId: string;
  anchor?: string;
  open: boolean;
};

const PostDetailPreview = ({
  postId,
  anchor,
  onPreviewLoadChange,
  onClick,
  queryId,
  resultId,
  position,
  open,
}: PostDetailPreviewProps) => {
  const isPublicView = useIsPublicView();
  const postContentRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number>();

  const [readPost] = useMutation(READ_POST_MUTATION);

  const { data: postData } = useQuery(POST_DETAIL_PREVIEW_QUERY, {
    variables: { id: postId ?? '', queryId, resultId, position },
    skip: !postId || isPublicView,
  });

  const { data: publicPost } = useQuery(PUBLIC_POST_DETAIL_PREVIEW_QUERY, {
    variables: { id: postId ?? '' },
    skip: !postId || !isPublicView,
  });

  const topicMap = useAllTopicMap();
  const post = postData?.post ?? publicPost?.post;
  const topics = populateObjects(
    topicMap,
    post?.topics.map(({ id }) => id),
  );

  const content = post?.content;

  const bannerImage = post?.banner?.original;

  useLayoutEffect(() => {
    if (postContentRef.current) {
      const setHeight = () =>
        setContentHeight(
          postContentRef?.current?.getBoundingClientRect().height,
        );
      const resizeObserver = new ResizeObserver(setHeight);
      resizeObserver.observe(postContentRef.current);
      setHeight();

      return () => resizeObserver.disconnect();
    }
    return undefined;
  }, [content]);

  const isPreviewLoaded = !!content;
  useEffect(() => {
    if (onPreviewLoadChange) {
      onPreviewLoadChange(isPreviewLoaded);
    }
  }, [onPreviewLoadChange, isPreviewLoaded]);

  useEffect(() => {
    const readStart = Date.now();

    return () => {
      const time = Date.now() - readStart;
      if (open && time >= MIN_ACTIVITY_READ) {
        readPost({ variables: { postId, time, queryId, resultId } }).catch(
          () => {},
        );
      }
    };
  }, [open, readPost, postId, queryId, resultId]);

  if (!post || !isPreviewLoaded) {
    return null;
  }

  const isPubliclyAccessible = post
    ? isAllowedThroughLinkAccess(
        PostPolicyAction.ViewPost,
        post,
        UserRole.Anonymous,
      )
    : false;

  const path =
    isPublicView && isPubliclyAccessible
      ? publicPostPath(post, anchor)
      : postPath(post, anchor);

  return (
    <div className={styles.container}>
      <NestableLink
        ignoreInsideLinks
        className={styles.link}
        to={path}
        onClick={onClick}
      >
        {post && post.__typename === 'Post' && (
          <PostDetailPreviewStateBar post={post} />
        )}
        {bannerImage && <PostDetailPreviewBanner banner={bannerImage} />}
        <div
          className={styles.postContentContainer}
          style={{ height: contentHeight }}
        >
          <div
            ref={postContentRef}
            className={classNames(styles.postContent, {
              [styles.attached]:
                ((post.__typename === 'Post' && hasStateBar(post)) ||
                  bannerImage) &&
                hasBreadcrumbs(topics),
            })}
          >
            <PostTopicListWithBreadcrumbs postTopics={topics} editing={false} />
            <PostPreviewQuillComponent
              content={content}
              anchor={anchor}
              rootClassName={styles.editorRoot}
            />
          </div>
        </div>
      </NestableLink>
    </div>
  );
};

export default PostDetailPreview;
